import React, { forwardRef, HTMLAttributes, useRef, useState } from "react";
import type { UniqueIdentifier } from "@dnd-kit/core";
import classNames from "classnames";
import { Popover, Input, message } from "antd";
import "antd/es/popover/style/css";
import "antd/es/input/style/css";
import "antd/es/message/style/css";
import styles from "./Page.module.css";
import { PageItem } from "@/constants/workbench";
import Markdown from "./Markdown";
import { md } from "@/utils/markdownHelper";
import IconFont from "@meitu/xdesign-icon-font";
import { statTrack } from "@/utils/mtstat";

export enum Position {
  Before = -1,
  After = 1,
}

export interface Props extends Omit<HTMLAttributes<HTMLDivElement>, "id"> {
  active?: boolean;
  clone?: boolean;
  insertPosition?: Position;
  id: UniqueIdentifier;
  index?: number;
  onRemove?(): void;
  onAdd?(): void;
  onMdBlur?(): void;
  onCopy?(): void;
  className?: string;
  item?: PageItem;
  onMdChange?: (markdown: string, html: string, len: number) => void;
  onInputChange?: (value: string, type: "title" | "end") => void;
  onInputBlur?: () => void;
}

export const Page = forwardRef<HTMLLIElement, Props>(function Page(
  {
    id,
    index,
    active,
    clone,
    insertPosition,
    onRemove,
    onAdd,
    onCopy,
    style,
    className,
    item,
    onMdChange,
    onMdBlur,
    onInputChange,
    onInputBlur,
    ...props
  },
  ref
) {
  const [focus, setFocus] = useState(false);
  const isComposing = useRef(false);

  const handleComposition = (
    e: React.CompositionEvent<HTMLInputElement>,
    type: "title" | "end"
  ) => {
    if (e.type === "compositionstart") {
      isComposing.current = true;
    }
    if (e.type === "compositionend") {
      isComposing.current = false;
      if (e.target instanceof HTMLInputElement) {
        onInputChange?.(e.target.value, type);
      }
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "title" | "end"
  ) => {
    if (!isComposing.current) {
      onInputChange?.(e.target.value, type);
    }
  };

  const content = (
    <div className={styles.popoverContent}>
      <div
        onClick={() => {
          statTrack("tools_click_ppt_home", {
            click_type:
              item?.type === "内容"
                ? "delete_page_main"
                : item?.type === "封面"
                ? "delete_page_cover"
                : "delete_page_final",
          });
          onRemove?.();
        }}
      >
        <IconFont name="TrashCan" fontSize={16} />
        删除
      </div>
      {item?.type === "内容" && (
        <div
          onClick={() => {
            statTrack("tools_click_ppt_home", {
              click_type: "add_page_more",
            });
            onAdd?.();
          }}
        >
          <IconFont name="PlusCircle" fontSize={16} />
          新增
        </div>
      )}
      {item?.type === "内容" && (
        <div
          onClick={() => {
            statTrack("tools_click_ppt_home", {
              click_type: "copy_page",
            });
            onCopy?.();
          }}
        >
          <IconFont name="Duplicate" fontSize={16} />
          复制
        </div>
      )}
    </div>
  );

  const onMdFocus = () => {
    setFocus(true);
  };

  return (
    <li
      className={classNames(
        styles.wrapper,
        item?.type !== "内容" && styles.fixed,
        active && styles.active,
        clone && styles.clone,
        insertPosition === Position.Before && styles.insertBefore,
        insertPosition === Position.After && styles.insertAfter,
        item?.content === "" && item.type === "内容" && styles.empty,
        focus && styles.focus,
        className
      )}
      style={style}
      ref={ref}
    >
      <div className={styles.page} data-id={item?.id}>
        <div
          className={classNames(styles.dragIcon, {
            [styles.dragIconVisible]: item?.type === "内容",
          })}
          {...(item?.type === "内容" ? { ...props } : {})}
        >
          {item?.type === "内容" ? (
            <div
            // style={{
            //   cursor: "grab",
            // }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                style={{
                  cursor: item?.type === "内容" ? "grab" : "default",
                  pointerEvents: item?.type === "内容" ? "auto" : "none",
                }}
              >
                <circle cx="6" cy="4" r="1" fill="#ABADB2" />
                <circle cx="10" cy="4" r="1" fill="#ABADB2" />
                <circle cx="10" cy="8" r="1" fill="#ABADB2" />
                <circle cx="10" cy="12" r="1" fill="#ABADB2" />
                <circle cx="6" cy="8" r="1" fill="#ABADB2" />
                <circle cx="6" cy="12" r="1" fill="#ABADB2" />
              </svg>
              {index !== void 0 ? (
                <span className={styles.number}>{index}</span>
              ) : null}
            </div>
          ) : (
            <span
              className={styles.tag}
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "100%",
                textAlign: "center",
              }}
            >
              {item?.type === "封面" ? "封面" : "结语"}
            </span>
          )}
        </div>
        <div className={styles.content}>
          {item?.type === "内容" ? (
            <Markdown
              value={md.render(item?.content || "")}
              onChange={onMdChange}
              autoFocus={index === 1}
              onBlur={() => {
                setFocus(false);
                onMdBlur?.();
              }}
              onFocus={onMdFocus}
            />
          ) : (
            <div className={styles.contentOther}>
              {item?.type === "封面" ? (
                <Input
                  defaultValue={item?.title}
                  autoFocus
                  onChange={(e) => handleChange(e, "title")}
                  onCompositionStart={(e) => handleComposition(e, "title")}
                  onCompositionEnd={(e) => handleComposition(e, "title")}
                  onBlur={() => {
                    onInputBlur?.();
                    setFocus(false);
                  }}
                  onFocus={onMdFocus}
                  // status={(item?.title?.length || 0) > 20 ? "error" : undefined}
                />
              ) : (
                <Input
                  defaultValue={item?.content}
                  onChange={(e) => handleChange(e, "end")}
                  onCompositionStart={(e) => handleComposition(e, "end")}
                  onCompositionEnd={(e) => handleComposition(e, "end")}
                  onBlur={() => {
                    onInputBlur?.();
                    setFocus(false);
                  }}
                  onFocus={onMdFocus}
                  // status={
                  //   (item?.content?.length || 0) > 20 ? "error" : undefined
                  // }
                />
              )}
            </div>
          )}
        </div>
        <Popover
          content={content}
          placement="bottomLeft"
          showArrow={false}
          overlayClassName={styles.popover}
          align={{ offset: [0, -6] }}
        >
          <div className={styles.action}>...</div>
        </Popover>
      </div>
    </li>
  );
});
